import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import {
  Target,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  MessageSquare,
} from "lucide-react";
import axios from "axios";

// Import general assessment components
import GridBackground from "../components/GridBackground";
import ContinueButton from "../components/ContinueButton";
import RatingScale from "../components/RatingScale";
import RadarChart from "../components/RadarChart";

// Import life-score specific components
import HeaderBar from "./HeaderBar";
import PhoneInputForm from "./PhoneInputForm";
import { categories, sections } from "./categories";

// Main Assessment Component
const LifeScoreAssessment = () => {
  const { userId } = useParams(); // Get userId from URL path
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scores, setScores] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  // removed external hovered state; handled inside RatingScale
  const [showIntro, setShowIntro] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentCategory = categories[currentIndex];

  // Clear any sticky focus that mobile browsers may retain when the step changes
  useEffect(() => {
    if (
      typeof document !== "undefined" &&
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }
  }, [currentIndex]);

  const handleRatingSelect = (rating) => {
    const updatedScores = {
      ...scores,
      [currentCategory.key]: rating,
    };

    setScores(updatedScores);
    // Ensure no element remains focused on mobile to avoid sticky styles
    if (
      typeof document !== "undefined" &&
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }

    // Auto advance after a short delay
    setTimeout(() => {
      if (currentIndex < categories.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setIsComplete(true);
        // If user is from coach system (has userId in URL), auto-submit results
        if (userId) {
          // Pass the updated scores to ensure Safety is included
          submitResultsWithScores(updatedScores);
        }
      }
    }, 500);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const submitResultsWithScores = async (finalScores) => {
    if (!userId || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const stats = calculateStatsWithScores(finalScores);

      const payload = {
        userId,
        scores: finalScores,
        stats: {
          average: stats.average,
          categoryAverages: stats.categoryAverages,
        },
        timestamp: new Date().toISOString(),
      };

      const response = await axios.post(
        "/api/assessments/wheel-of-life/submit",
        payload
      );

      if (response.status === 200) {
        console.log("Assessment results submitted successfully");
        // The backend will handle sending the coach message
      }
    } catch (error) {
      console.error("Error submitting assessment results:", error);
      // Optionally show error to user or retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneSubmit = (phoneNumber) => {
    window.location.href = `/coach?phone=${encodeURIComponent(phoneNumber)}`;
  };

  const calculateStatsWithScores = (scoresData) => {
    const values = Object.values(scoresData);
    const average =
      values.reduce((sum, score) => sum + score, 0) / values.length;

    // Find strongest and weakest areas
    const categoryAverages = {};
    sections.forEach((section) => {
      const sectionScores = categories
        .filter((cat) => cat.category === section)
        .map((cat) => scoresData[cat.key] || 0);
      categoryAverages[section] =
        sectionScores.reduce((sum, score) => sum + score, 0) /
        sectionScores.length;
    });

    const strongest = Object.entries(categoryAverages).reduce((a, b) =>
      categoryAverages[a[0]] > categoryAverages[b[0]] ? a : b
    )[0];
    const weakest = Object.entries(categoryAverages).reduce((a, b) =>
      categoryAverages[a[0]] < categoryAverages[b[0]] ? a : b
    )[0];

    // Prepare data for radar chart
    const radarData = categories.map((cat) => ({
      name: cat.name,
      score: scoresData[cat.key] || 0,
      color: cat.color,
      category: cat.category,
    }));

    return { average, radarData, strongest, weakest, categoryAverages };
  };

  const calculateStats = () => {
    return calculateStatsWithScores(scores);
  };

  // Intro Screen
  if (showIntro) {
    return (
      <div className="flex flex-col h-screen relative overflow-hidden">
        <GridBackground />

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            className="flex flex-col items-center justify-center px-6 max-w-md text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="mb-8 relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="w-16 h-16 flex items-center justify-center relative">
                <div className="absolute inset-0 rounded-full border border-[#dcff0250] bg-[#dcff021A]" />
                <Target className="w-8 h-8 text-[#dcff02]" />
              </div>
            </motion.div>

            <motion.h1
              className="text-3xl font-medium text-white mb-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              Discover Your LIFE Score
            </motion.h1>

            <motion.p
              className="text-lg text-white/80 mb-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              Rate 12 key areas of your life to get personalized insights.
            </motion.p>
          </motion.div>
        </div>

        <ContinueButton onClick={() => setShowIntro(false)}>
          I'M READY
        </ContinueButton>
      </div>
    );
  }

  // Results Screen
  if (isComplete) {
    const stats = calculateStats();

    return (
      <div className="min-h-screen relative overflow-hidden">
        <GridBackground />

        {/* Scrollable content */}
        <div className="relative z-10 min-h-screen">
          <div className="flex items-center justify-center py-16 px-8">
            <motion.div
              className="text-center space-y-12 max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Contextual Header */}
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
                  {/* Strongest Area */}
                  <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 px-4 py-2 rounded-full">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm font-medium">
                      Strongest: {stats.strongest}
                    </span>
                  </div>

                  {/* Weakest Area */}
                  <div className="flex items-center gap-2 bg-red-500/20 border border-red-500/30 px-4 py-2 rounded-full">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    <span className="text-red-400 text-sm font-medium">
                      Focus: {stats.weakest}
                    </span>
                  </div>

                  {/* SMS Notice */}
                  <div className="flex items-center gap-2 bg-[#dcff02]/10 border border-[#dcff02]/30 px-3 py-1.5 rounded-full">
                    <MessageSquare className="w-4 h-4 text-[#dcff02]" />
                    <span className="text-[#dcff02] text-sm font-medium">
                      1440 will text you based on your results
                    </span>
                  </div>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  Your LIFE Score:{" "}
                  <span className="text-[#dcff02]">
                    {stats.average.toFixed(1)}
                  </span>
                </h1>

                <p className="text-white/60 text-lg max-w-md mx-auto">
                  {stats.average >= 8
                    ? "You're thriving across most areas of life!"
                    : stats.average >= 6
                    ? "Strong foundation with room for targeted growth."
                    : stats.average >= 4
                    ? "Significant opportunities for improvement ahead."
                    : "Let's work together to transform your life."}
                </p>
              </motion.div>

              {/* Results Card with Radar Chart */}
              <motion.div
                className="bg-black/40 border border-white/10 p-8 rounded-2xl backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <RadarChart data={stats.radarData} />

                {/* Category Insights */}
                <div className="grid grid-cols-2 gap-4 mt-8">
                  {Object.entries(stats.categoryAverages).map(
                    ([category, score]) => {
                      const isStrong = score >= 7;
                      const isWeak = score < 5;

                      return (
                        <div key={category} className="text-center">
                          <div className="text-sm text-white/60 mb-1">
                            {category}
                          </div>
                          <div
                            className={`text-xl font-bold ${
                              isStrong
                                ? "text-green-400"
                                : isWeak
                                ? "text-red-400"
                                : "text-[#dcff02]"
                            }`}
                          >
                            {score.toFixed(1)}
                            {isStrong ? (
                              <TrendingUp className="inline w-4 h-4 ml-1" />
                            ) : isWeak ? (
                              <TrendingDown className="inline w-4 h-4 ml-1" />
                            ) : (
                              <Minus className="inline w-4 h-4 ml-1" />
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Conditional Form - Only show if user didn't come from coach */}
          {!userId && <PhoneInputForm onSubmit={handlePhoneSubmit} />}

          {/* If user came from coach, show different message */}
          {userId && (
            <div className="w-full max-w-2xl mx-auto px-8 py-12 text-center">
              {isSubmitting ? (
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dcff02] mx-auto"></div>
                  <p className="text-white/80">
                    Sending results to your coach...
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-12 h-12 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto">
                    <svg
                      className="w-6 h-6 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <h3 className="text-white font-medium text-xl">
                    Results Sent!
                  </h3>
                  <p className="text-white/60">
                    Your LIFE Score results have been shared with your 1440
                    coach. They'll follow up with personalized insights shortly.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Assessment Screen
  return (
    <div className="flex flex-col h-screen relative overflow-hidden">
      <GridBackground />

      <HeaderBar currentIndex={currentIndex} />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-8 pb-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            className="text-center space-y-16 max-w-sm"
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -15 }}
            transition={{ duration: 0.4 }}
          >
            {/* Category Icon & Name */}
            <div className="space-y-6">
              <motion.div
                className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto"
                style={{
                  backgroundColor: `${currentCategory.color}20`,
                  border: `2px solid ${currentCategory.color}40`,
                }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <currentCategory.icon
                  className="w-10 h-10"
                  style={{ color: currentCategory.color }}
                />
              </motion.div>

              <div>
                <h2 className="text-3xl font-bold text-white mb-2">
                  {currentCategory.name}
                </h2>
                <p className="text-base text-white/60">
                  {currentCategory.description}
                </p>
              </div>
            </div>

            {/* Rating Scale */}
            <RatingScale
              key={currentIndex}
              onRatingSelect={handleRatingSelect}
              selectedRating={scores[currentCategory.key]}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Previous Button Only */}
      {currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="fixed bottom-6 left-6 flex items-center gap-2 px-4 py-2 rounded-lg 
                     text-white/60 hover:text-white hover:bg-white/10 transition-colors z-50"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Previous</span>
        </button>
      )}
    </div>
  );
};

export default LifeScoreAssessment;
